import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MsalProvider } from "@azure/msal-react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { msalInstance } from './auth/msalInstance';
import Main from './Main';
import ProfilePage from './ProfilePage';
import AdminPage from './admin/AdminPage';
import { BillingCancelPage, BillingSuccessPage } from './BillingReturnPages';
import '../css/App.css';
import Spinner from './spinner/Spinner';
import Header from './Header';
import Sidebar from './Sidebar';
import { AuthProvider } from './authProvider/AuthContext';
import FeedbackButton from './FeedbackButton';

// Initialize MSAL and handle account state
/**
 * Tracks whether the viewport is in "compact nav" mode (below 500px wide),
 * where the persistent sidebar rail is replaced by a header hamburger +
 * off-canvas drawer.
 */
const COMPACT_NAV_QUERY = '(max-width: 499px)';

function useCompactNav() {
    const [isCompact, setIsCompact] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia(COMPACT_NAV_QUERY).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mql = window.matchMedia(COMPACT_NAV_QUERY);
        const handler = (event) => setIsCompact(event.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return isCompact;
}

const initializeMsal = async () => {
    try {
        await msalInstance.initialize();

        // Complete any redirect from login or clear error/hash left by silent flows.
        const redirectResult = await msalInstance.handleRedirectPromise();
        if (redirectResult?.account) {
            msalInstance.setActiveAccount(redirectResult.account);
            // Capture the idToken immediately after login — stored for explicit
            // sign-out (logoutRedirect idTokenHint) and similar flows.
            if (redirectResult.idToken) {
                sessionStorage.setItem('msal.lastIdToken', redirectResult.idToken);
            }
        } else if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
            msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
        }

        return true;
    } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        return false;
    }
};

function AppContent() {
    const navigate = useNavigate();
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [sidebarRefresh, setSidebarRefresh] = useState(null);
    const [resetMainFunction, setResetMainFunction] = useState(null);
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const isCompactNav = useCompactNav();
    const prevCompactRef = useRef(isCompactNav);

    useEffect(() => {
        if (isCompactNav !== prevCompactRef.current) {
            // When crossing between desktop rail and compact drawer, always
            // collapse so we never strand a full-screen drawer or a wide rail.
            setSidebarExpanded(false);
            prevCompactRef.current = isCompactNav;
        }
    }, [isCompactNav]);

    const toggleSidebar = useCallback(() => {
        setSidebarExpanded((prev) => !prev);
    }, []);

    const closeSidebar = useCallback(() => {
        setSidebarExpanded(false);
    }, []);

    const handleBlogSelect = useCallback((blog) => {
        setSelectedBlog(blog);
        navigate('/'); // Navigate to main page
    }, [navigate]);

    const clearSelectedBlog = useCallback(() => {
        setSelectedBlog(null);
    }, []);

    const handleSidebarRefresh = useCallback((refreshFunction) => {
        setSidebarRefresh(() => refreshFunction);
    }, []);

    const refreshSidebar = useCallback(() => {
        if (sidebarRefresh) {
            sidebarRefresh();
        }
    }, [sidebarRefresh]);

    const handleMainReset = useCallback((resetFunction) => {
        setResetMainFunction(() => resetFunction);
    }, []);

    const resetToStart = useCallback(() => {
        if (resetMainFunction) {
            resetMainFunction();
        }
        navigate('/'); // Ensure we're on main page
    }, [resetMainFunction, navigate]);

    const appClassName = [
        'App',
        sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed',
        isCompactNav ? 'App--compact-nav' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={appClassName}>
            <Sidebar
                onBlogSelect={handleBlogSelect}
                onSidebarRefresh={handleSidebarRefresh}
                expanded={sidebarExpanded}
                onToggle={toggleSidebar}
                onClose={closeSidebar}
                compact={isCompactNav}
            />
            <div className="app-content">
                <Header
                    onLogoClick={resetToStart}
                    showMenuButton={isCompactNav}
                    onMenuClick={toggleSidebar}
                    menuOpen={sidebarExpanded}
                />
                <Spinner />
                <Routes>
                    <Route 
                        path="/" 
                        element={
                            <Main 
                                selectedBlog={selectedBlog} 
                                clearSelectedBlog={clearSelectedBlog}
                                refreshSidebar={refreshSidebar}
                                onResetRegister={handleMainReset}
                            />
                        } 
                    />
                    <Route 
                        path="/profile" 
                        element={
                            <ProfilePage 
                                onBlogSelect={handleBlogSelect}
                                refreshSidebar={refreshSidebar}
                            />
                        } 
                    />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/billing/success" element={<BillingSuccessPage />} />
                    <Route path="/billing/cancel" element={<BillingCancelPage />} />
                </Routes>
                <footer className="app-credit" aria-label="App credit">
                    Built by{' '}
                    <a
                        href="https://gitcity.nl"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="GitCity — gitcity.nl"
                    >
                        GitCity
                    </a>
                </footer>
            </div>
            <FeedbackButton />
        </div>
    );
}

function App() {
    const [msalReady, setMsalReady] = useState(false);
    const [initError, setInitError] = useState(null);

    useEffect(() => {
        const init = async () => {
            try {
                const initialized = await initializeMsal();
                if (initialized) {
                    setMsalReady(true);
                } else {
                    setInitError('Failed to initialize authentication. Please refresh the page.');
                }
            } catch (error) {
                console.error('MSAL initialization error:', error);
                setInitError('Authentication initialization failed. Please refresh the page.');
            }
        };

        init();
    }, []);

    if (initError) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <h2>Authentication Error</h2>
                <p>{initError}</p>
                <button onClick={() => window.location.reload()}>
                    Refresh Page
                </button>
            </div>
        );
    }

    if (!msalReady) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <div>Initializing authentication...</div>
            </div>
        );
    }

    return (
        <MsalProvider instance={msalInstance}>
            <AuthProvider>
                <Router>
                    <AppContent />
                </Router>
            </AuthProvider>
        </MsalProvider>
    );
}

export default App;
